import React, { Component } from 'react';
import PropTypes from 'prop-types';
import '@babel/polyfill';
import { Route, Switch } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import svgxuse from 'svgxuse';
import smoothscroll from 'smoothscroll-polyfill';

import { getOnlineConfig, redirectToApi, getAuthState } from 'actions/app';
import { AuthState } from 'actions';

import EnvConfig from 'env.config';

import { createBrowserHistory } from 'history';

import Home from 'containers/Home';
import Form from 'containers/Form';

import AppProvider from 'components/AppProvider';
import Notifications from 'components/Common/Notifications';
import ErrorBoundary from 'components/Errors/ErrorBoundary';
import NotFoundError from 'components/Errors/NotFoundError';
import ServerError from 'components/Errors/ServerError';
import InternetConnectionError from 'components/Errors/InternetConnectionError';
import SessionTimeoutError from 'components/Errors/SessionTimeoutError';
import UnsupportedError from 'components/Errors/UnsupportedError';
import UnauthorisedError from 'components/Errors/UnauthorisedError';
import UnlicensedError from 'components/Errors/UnlicensedError';
import ServiceUnavailableError from 'components/Errors/ServiceUnavailableError';
import SuccessPage from 'components/SuccessPage';
import FooterLinkPage from 'components/Footer/FooterLinkPage';
import LogoutPage from 'components/LogoutPage';
import AuthentaLinkPinConfirmation from 'components/AuthentaLink/PinConfirmation';
import AuthentaLinkReferenceConfirmation from 'components/AuthentaLink/ReferenceConfirmation';
import DefaultFormDisplay from 'components/Forms/DefaultFormDisplay';

import Header from 'components/Header';
import Footer from 'components/Footer';
import CookieBanner from 'components/CookieBanner';
// import StyleGuide from 'components/StyleGuide';
import FormSelection from 'containers/FormSelection';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import KeepAlive from 'components/KeepAlive';
import Eula from 'components/Modals/Eula';
import PreQualifyingQuestionnaire from 'containers/PreQualifyingQuestionnaire';

import 'components/IconLibrary';

import '../../scss/vendors/bootstrap.scss';
import '../../scss/app.global.scss';
import '../../scss/branding/cairoamman.scss';

const history = createBrowserHistory();
export { history };
export { svgxuse };

const unprotectedRoutes = [
  '/loggedout',
  'login',
  '/unauthorized',
  '/unlicensed',
  '/server',
  '/notfound',
  '/unsupported',
  '/no-internet',
  '/links/privacy',
  '/links/cookies',
  '/links/termsOfUse',
  '/authentalink',
  '/confirmPin'
];

const confirmAppStateToProceed = [
  '/authentalink'
];

// A wrapper for <Route> that displays loading
// screen if you're not yet authenticated.
const ProtectedRoute = ({ isAllowed, ...props }) => (
  isAllowed ? <Route { ...props } /> : <div className="loading-container"><LoadingWrapper text="We are getting the site ready.  Please wait..." /></div>
);

ProtectedRoute.propTypes = {
  isAllowed: PropTypes.bool.isRequired
};

/* eslint-disable react/prefer-stateless-function */
class App extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isEulaAccepted: PropTypes.bool.isRequired,
    bypassEula: PropTypes.bool.isRequired,
    defaultFormType: PropTypes.string,
    customerName: PropTypes.string,
    authState: PropTypes.string
  };

  static defaultProps = {
    defaultFormType: null,
    customerName: null,
    authState: AuthState.Unknown
  }

  static hasAppCookies() {
    if (document.cookie && document.cookie.split(';').filter(item => item.trim().startsWith(EnvConfig.cookieConsentName)).length) {
      return true;
    }

    return false;
  }

  static isFormPage() {
    return window.location.pathname.includes('/forms');
  }

  static isUadLink = () => window.location.pathname.startsWith('/uad/');

  componentDidMount() {
    const { dispatch } = this.props;

    smoothscroll.polyfill();

    if (App.isUadLink()) {
      redirectToApi(window.location.pathname);
    }

    if (!unprotectedRoutes.includes(window.location.pathname) && !App.isUadLink()) {
      dispatch(getOnlineConfig());
    }

    if (confirmAppStateToProceed.includes(window.location.pathname)) {
      dispatch(getAuthState());
    }
  }

  get hasDefaultFormType() {
    const { defaultFormType } = this.props;
    return defaultFormType !== null;
  }

  get canAccessMainRoutes() {
    const { isEulaAccepted, bypassEula } = this.props;
    return (!App.isUadLink() && (bypassEula || isEulaAccepted || unprotectedRoutes.includes(window.location.pathname)));
  }

  render() {
    const { isAuthenticated, isEulaAccepted, customerName, authState } = this.props;

    return (
      <ErrorBoundary origin="App">
        <ConnectedRouter history={ history }>
          <AppProvider>
            <React.Fragment>
              <Link className="skip-nav" to="#main">Skip to main content</Link>
              <Header showMenu={ isEulaAccepted } customerName={ customerName } />
              <main id="main" className={ isAuthenticated ? 'auth' : '' }>
                <div className="container">
                  <Notifications />
                </div>
                {this.canAccessMainRoutes && <div id="main-container" className="container main-content">
                  <ErrorBoundary origin="Page">
                    <KeepAlive />
                    { this.hasDefaultFormType && !App.isFormPage() && <DefaultFormDisplay /> }
                    <Switch>
                      <ProtectedRoute isAllowed={ isAuthenticated && !this.hasDefaultFormType } exact path="/" component={ Home } />
                      <ProtectedRoute isAllowed={ isAuthenticated && !this.hasDefaultFormType } exact path="/form-selection" component={ FormSelection } />
                      <ProtectedRoute isAllowed={ isAuthenticated } exact path="/forms/:formType/:formId" component={ Form } />
                      <ProtectedRoute isAllowed={ isAuthenticated } exact path="/forms/:formType/:formId/success" component={ SuccessPage } />
                      <ProtectedRoute isAllowed={ isAuthenticated && !this.hasDefaultFormType } exact path="/pre-qualifying-questionnaire" component={ PreQualifyingQuestionnaire } />
                      <Route exact path="/loggedout" component={ LogoutPage } />
                      <Route exact path="/unauthorized" component={ UnauthorisedError } />
                      <Route exact path="/unlicensed" component={ UnlicensedError } />
                      <Route exact path="/links/:pageType" component={ FooterLinkPage } />
                      { /* <Route exact path="/style-guide" component={ StyleGuide } /> */ }
                      <Route exact path="/no-internet" component={ InternetConnectionError } />
                      <Route exact path="/unsupported" component={ UnsupportedError } />
                      <Route exact path="/server" component={ ServerError } />
                      <Route exact path="/sessiontimeout" component={ SessionTimeoutError } />
                      <Route exact path="/unavailable" component={ ServiceUnavailableError } />
                      <ProtectedRoute isAllowed={ authState !== AuthState.Unknown && authState !== AuthState.Requesting } exact path="/authentalink" component={ AuthentaLinkReferenceConfirmation } />
                      <Route exact path="/confirmPin" component={ AuthentaLinkPinConfirmation } />
                      <Route component={ NotFoundError } />
                    </Switch>
                  </ErrorBoundary>
                </div>}
                { !App.hasAppCookies() && <CookieBanner /> }
                { isAuthenticated && !unprotectedRoutes.includes(window.location.pathname) && <Eula /> }
                { App.isUadLink() && <div className="loading-container"><LoadingWrapper text="We are getting the site ready.  Please wait..." /></div> }
              </main>
              { this.canAccessMainRoutes && <Footer /> }
            </React.Fragment>
          </AppProvider>
        </ConnectedRouter>
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = dispatch => ({ dispatch });

function mapStateToProps(state) {
  const {
    app: {
      isAuthenticated,
      isEulaAccepted,
      bypassEula,
      onlineConfig,
      authState
    },
  } = state;

  return {
    isAuthenticated,
    isEulaAccepted,
    bypassEula,
    defaultFormType: onlineConfig ? onlineConfig.defaultFormType : null,
    customerName: onlineConfig ? onlineConfig.customerName : null,
    authState
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(App);
